import React, { useEffect, useState } from "react";
import { Collection } from "../../api/collections/types";
import CollectionQuantitySelector from "./collection-quantity-selector";
import { getCurrentStage } from "utils/helpers/collectionHelper";
import {
  CrossmintProvider,
  CrossmintCheckoutProvider,
  CrossmintHostedCheckout,
  useCrossmintCheckout,
} from "@crossmint/client-sdk-react-ui";
import { useAppContext } from "context/AppContextProvider";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";

interface CollectionMintProps {
  collection: Collection;
}

interface CrossmintCheckoutWithTrackingProps {
  lineItems: React.ComponentProps<typeof CrossmintHostedCheckout>["lineItems"];
  payment: React.ComponentProps<typeof CrossmintHostedCheckout>["payment"];
  recipient?: React.ComponentProps<typeof CrossmintHostedCheckout>["recipient"];
  appearance?: React.ComponentProps<typeof CrossmintHostedCheckout>["appearance"];
  locale?: React.ComponentProps<typeof CrossmintHostedCheckout>["locale"];
}

// Inner component that can safely use the hook (is wrapped by CrossmintCheckoutProvider)
const CrossmintCheckoutWithTracking: React.FC<CrossmintCheckoutWithTrackingProps> = ({
  lineItems,
  payment,
  recipient,
  appearance,
  locale,
}) => {
  const { order } = useCrossmintCheckout();

  useEffect(() => {
    if (order && order.phase === "completed") {
      // Purchase completed
      // eslint-disable-next-line no-console
      console.log("Purchase completed!");

      enqueueSnackbar("Your purchase was successful.", {
        key: new Date().getTime(),
        persist: true
      });
    }
  }, [order]);

  return (
    <CrossmintHostedCheckout
      lineItems={lineItems}
      payment={payment}
      recipient={recipient}
      appearance={appearance}
      locale={locale}
    />
  );
};

const CollectionMint: React.FC<CollectionMintProps> = ({ collection }) => {
  const { t } = useTranslation();
  const { authUser } = useAppContext();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const stage = getCurrentStage(collection?.stages || [], userTimeZone);
  const collectionContract = collection.contracts?.[0] ?? null;

  const [quantity, setQuantity] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);

  useEffect(() => {
    if (stage) {
      setQuantity(1);
      setTotalPrice(parseFloat(stage.sale_price));
    }
  }, [stage]);

  if (!stage || !collectionContract) {
    return null;
  }

  const onChange = (value: number) => {
    setQuantity(value);
    const price = parseFloat(stage.sale_price) * value;
    setTotalPrice(parseFloat(price.toFixed(5)));
  };

  // -------------------------------------------
  // Crossmint configuration (v3 Hosted Checkout)
  // -------------------------------------------

  const clientApiKey = process.env.REACT_APP_CROSSMINT_CLIENT_API_KEY ?? "";

  // Collection ID is always dynamic from DB
  const collectionId = collectionContract.cm_collection_id ?? "";

  if (!clientApiKey || !collectionId) {
    // If Crossmint is not properly configured, avoid rendering a broken checkout
    return (
      <div className="row">
        <div className="col-auto text-center mt-4 pt-2">
          <div className="d-flex justify-content-center align-items-center">
            <CollectionQuantitySelector
              min={1}
              max={collectionContract.total_available}
              onChange={onChange}
            />
            <button
              className="xmint-btn btn btn-primary btn-primary-lg ms-4"
              disabled
            >
              {t(
                "common.errors.config.crossmint",
                "Crossmint is not configured"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const collectionLocator = `crossmint:${collectionId}`;

  // callData must match your mint function arguments (without the "to" address)
  const callData = {
    totalPrice: totalPrice.toString(),

    // REQUIRED BY YOUR SMART CONTRACT
    _qty: quantity.toString(),

    // Your custom contract argument
    type: collectionContract.token_standard?.name?.toLowerCase(),

    // Optional tokenId (if the collection is single-token)
    ...(collectionContract.token_number && {
      _tokenId: collectionContract.token_number,
    }),

    // Metadata replaces old whPassThroughArgs
    metadata: {
      contract_id: collectionContract.id,
      qty: quantity,
      ...(authUser?.id ? { user_id: authUser.id } : {}),
    },
  };

  const recipientEmail = authUser?.email ?? "";
  const recipientWallet = authUser?.wallets?.[0]?.wallet_address ?? "";

  const isDisabled =
    quantity === 0 || collectionContract.total_available === 0;

  // Default crypto payment preferences (Sepolia → still "ethereum")
  const chainName = collectionContract.chain?.name?.toLowerCase() ?? "";
  const chainNetwork = collectionContract.chain?.network?.toLowerCase() ?? "";

  let defaultChain: "ethereum" | "polygon" | "solana" = "ethereum";
  let defaultCurrency: "eth" | "matic" | "sol" | "usdc" = "eth";

  if (chainName.includes("polygon") || chainNetwork.includes("polygon")) {
    defaultChain = "polygon";
    defaultCurrency = "matic";
  } else if (chainName.includes("solana") || chainNetwork.includes("solana")) {
    defaultChain = "solana";
    defaultCurrency = "sol";
  }

  const lineItems: React.ComponentProps<
    typeof CrossmintHostedCheckout
  >["lineItems"] = {
    collectionLocator,
    callData,
  };

  const payment: React.ComponentProps<typeof CrossmintHostedCheckout>["payment"] =
    {
      crypto: {
        enabled: true,
        defaultChain,
        defaultCurrency,
      },
      fiat: {
        enabled: true,
        defaultCurrency: "usd",
      },
      defaultMethod: "fiat",
      receiptEmail: recipientEmail || undefined,
    };

  const recipient: React.ComponentProps<
    typeof CrossmintHostedCheckout
  >["recipient"] = recipientWallet
    ? { walletAddress: recipientWallet }
    : recipientEmail
    ? { email: recipientEmail }
    : undefined;

  const appearance: React.ComponentProps<
    typeof CrossmintHostedCheckout
  >["appearance"] = {
    display: "popup",
    overlay: {
      enabled: true,
    },
    variables: {
      colors: {
        accent: "#C9E200",
      },
    },
    theme: {
      button: "light",
      checkout: "dark",
    },
  };

  return (
    <div className="row">
      <div className="col-auto text-center mt-4 pt-2">
        <div className="d-flex justify-content-center align-items-center">
          <CollectionQuantitySelector
            min={1}
            max={collectionContract.total_available}
            onChange={onChange}
          />

          {isDisabled ? (
            <button
              className="xmint-btn btn btn-primary btn-primary-lg ms-4"
              disabled
            >
              {t("common.buttons.buy.soldOut", "Sold out")}
            </button>
          ) : (
            <CrossmintProvider apiKey={clientApiKey}>
              <CrossmintCheckoutProvider>
                <CrossmintCheckoutWithTracking
                  lineItems={lineItems}
                  payment={payment}
                  recipient={recipient}
                  appearance={appearance}
                  locale="en-US"
                />
              </CrossmintCheckoutProvider>
            </CrossmintProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionMint;
